// Copyright (c) 2023 shadcn
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights in Techsafe's modifications: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
//
// Derived work. Backported from shadcn/ui `tooltip.tsx`, as vendored in
// git.iot-ide.com/GizemA/nodecontroluisystem (`src/components/ui/tooltip.tsx`)
// at commit ff117e2075db7c59ca74afc2b48d96ec3fc127b2, and adapted.
// Modifications are stated in NOTICE. The original licence applies; see NOTICE.
//
// SPDX-License-Identifier: MIT
//
// Techsafe'in yaptigi degisiklikler uzerindeki mali haklar Techsafe
// Teknoloji ve Muhendislik A.S.'ye aittir; eser sahiplerinin manevi
// haklari saklidir (FSEK 5846). Orijinal eser kendi lisansi altindadir.
//
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Primitive component (shadcn/ui Tooltip, MIT) on the `radix-ui` Tooltip.
 * Adapted to the NCDS neutral tooltip (nodecontroluisystem
 * `primitive-components/usage-notes/tooltip.md`), behaviourally and visually
 * equal to AU0008's `components/ui/tooltip.tsx` (#67, parity):
 *  - `bg-foreground` / `text-background`, the `text-nc-*` type scale and a
 *    bounded width (`max-w-xs`) for 1–2 short sentences;
 *  - top placement by default, `sideOffset` 6, `collisionPadding` 8; Radix
 *    flips it and keeps it in the viewport when space runs out;
 *  - a plain `fill-foreground` arrow and no enter/exit animation classes
 *    (this widget loads no animation plugin);
 *  - `Tooltip` brings its own `TooltipProvider` (delay 0), so a caller needs
 *    no app-level provider;
 *  - the content is always rendered through a portal.
 */

import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 6,
  collisionPadding = 8,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          // Font size as `text-[length:…]`, not `text-nc-1`: tailwind-merge
          // does not know `text-nc-*` and drops it as a conflicting text
          // colour next to `text-background`, leaving the inherited size.
          "z-50 w-fit max-w-xs rounded-[var(--radius)] bg-foreground px-nc-3 py-nc-2 text-[length:var(--text-1)] leading-nc-2 text-background text-balance",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="fill-foreground" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
